import React, { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { apiReferencePageFooter } from './layout/footerConfigs'

interface CodeBlockProps {
  code: string
  language?: string
}

const CodeBlock: React.FC<CodeBlockProps> = ({ code }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="relative group my-4">
      <button
        onClick={handleCopy}
        className="absolute top-3 right-3 px-3 py-1 rounded-lg text-xs font-medium transition-all duration-300 opacity-0 group-hover:opacity-100"
        style={{
          backgroundColor: copied ? '#10B981' : 'rgba(255, 255, 255, 0.1)',
          color: '#FFFFFF',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>
      <pre
        className="p-4 rounded-xl overflow-x-auto"
        style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <code
          className="text-sm"
          style={{
            color: '#10B981',
            fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace"
          }}
        >
          {code}
        </code>
      </pre>
    </div>
  )
}

interface SectionProps {
  title: string
  children: React.ReactNode
  id?: string
}

const Section: React.FC<SectionProps> = ({ title, children, id }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0 })

  return (
    <motion.section
      ref={ref}
      id={id}
      className="mb-16"
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.6 }}
    >
      <h2
        className="text-3xl font-bold mb-6"
        style={{
          color: '#1A1A1A',
          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif"
        }}
      >
        {title}
      </h2>
      <div
        style={{
          color: '#666666',
          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
          lineHeight: '1.8'
        }}
      >
        {children}
      </div>
    </motion.section>
  )
}

const HeroSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.2, margin: "0px 0px -100px 0px" })

  return (
    <section ref={ref} className="relative flex items-center justify-center" style={{ backgroundColor: 'transparent', minHeight: '100vh' }}>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h1
          className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight"
          style={{
            color: '#1A1A1A',
            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
            fontWeight: 600,
            letterSpacing: '-0.03em'
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 50, scale: 0.9 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 50, scale: 0.9 }}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          JobMeet API
        </motion.h1>

        <motion.p
          className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed"
          style={{
            color: '#666666',
            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
            fontWeight: 500
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Your in-house scheduling system. Powerful, secure, and deeply integrated with Mernaki.
        </motion.p>
      </div>
    </section>
  )
}

const FooterSection: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  if (isMobile) {
    return <PageMobileFooter {...apiReferencePageFooter} />
  }

  return (
    <footer
      className="relative"
      style={{
        backgroundColor: '#0E0E0E',
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        paddingTop: '100px',
        paddingBottom: '40px',
        marginTop: '-60px'
      }}
    >
      {/* Floating Gradient Divider */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Columns */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Column 1 - Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img
                src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
                alt="Mernaki Logo"
                className="object-contain"
                style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
                loading="lazy"
              />
              <h3
                className="text-lg"
                style={{
                  color: '#FFFFFF',
                  fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                  fontWeight: 400
                }}
              >
                Mernaki
              </h3>
            </div>
            <p
              className="text-sm leading-relaxed"
              style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400
              }}
            >
              Native scheduling system for premium recruitment automation.
            </p>
          </div>

          {/* Column 2 - JobMeet API */}
          <div>
            <h3
              className="text-sm mb-4"
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em'
              }}
            >
              JobMeet API
            </h3>
            <ul className="space-y-2">
              {[
                { label: 'Overview', to: '#overview' },
                { label: 'Authentication', to: '#authentication' },
                { label: 'Meeting Types', to: '#meeting-types' },
                { label: 'Availability', to: '#availability' },
                { label: 'Bookings', to: '#bookings' },
                { label: 'Notifications', to: '#notifications' },
                { label: 'AI Summarization', to: '#ai-summarization' },
                { label: 'API Keys', to: '#api-keys' },
                { label: 'Smart Forms', to: '#smart-forms' },
                { label: 'Workflows', to: '#workflows' },
                { label: 'Webhooks', to: '#webhooks' },
                { label: 'Analytics', to: '#analytics' },
                { label: 'Integrations', to: '#integrations' },
                { label: 'User DB (BYODB)', to: '#user-db-config' },
                { label: 'Self-Hosting', to: '#self-hosting' },
                { label: 'Error Handling', to: '#errors' }
              ].map((item, i) => (
                <li key={i}>
                  <a
                    href={item.to}
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Company */}
          <div>
            <h3
              className="text-sm mb-4"
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em'
              }}
            >
              Company
            </h3>
            <ul className="space-y-2">
              {[
                { label: 'About', to: '/about' },
                { label: 'Privacy Policy', to: '/privacy-policy' },
                { label: 'Terms of Service', to: '/terms' },
                { label: 'Impressum', to: '/impressum' }
              ].map((item, i) => (
                <li key={i}>
                  <Link
                    to={item.to}
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 - Support & Developer Contact */}
          <div>
            <h3
              className="text-sm mb-4"
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em'
              }}
            >
              Support
            </h3>
            <ul className="space-y-2 mb-6">
              {[
                { label: 'Help Center', to: '/help' },
                { label: 'Report an Issue', to: '/report-issue' },
                { label: 'Contact Us', to: '/contact' }
              ].map((item, i) => (
                <li key={i}>
                  <Link
                    to={item.to}
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="space-y-2">
              <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', sans-serif" }}>
                Developer Support:
              </p>
              <a
                href="mailto:developer@mernaki.de"
                className="text-sm transition-all duration-300"
                style={{
                  color: '#4A90E2',
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500,
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#357ABD'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#4A90E2'}
              >
                developer@mernaki.de
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="pt-6 text-center" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <p
            className="text-sm"
            style={{
              color: 'rgba(255, 255, 255, 0.3)',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 300
            }}
          >
            © {new Date().getFullYear()} Mernaki. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}

export const JobMeetApiDocs: React.FC = () => {
  const isMobile = useIsMobile()
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      {/* Main Content Wrapper - Matching API Reference Page */}
      <div
        className="min-h-screen relative"
        style={{
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: '48px',
          borderBottomRightRadius: '48px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
        }}
      >
        {/* Animated Background Gradient Orbs */}
        <motion.div
          className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : { scale: [1, 1.2, 1], x: [0, 50, 0], y: [0, 30, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/2 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : { scale: [1, 1.3, 1], x: [0, -50, 0], y: [0, -30, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        />

        <HeroSection prefersReducedMotion={prefersReducedMotion.current} />

        {/* Content Section - Matching API Reference Layout */}
        <div className="relative mx-auto pb-16" style={{
          maxWidth: '1200px',
          marginTop: '-20vh',
          paddingLeft: '4rem',
          paddingRight: '4rem'
        }}>
          <div>
            {/* Overview Section */}
            <Section title="Overview" id="overview">
              <p className="mb-4">
                <strong>JobMeet</strong> is Mernaki's native scheduling system - a powerful, secure alternative to Calendly that's deeply integrated with your recruitment workflow.
              </p>
              <p className="mb-4">JobMeet provides a complete scheduling solution with:</p>
              <ul className="list-disc list-inside space-y-2 mb-6">
                <li><strong>Meeting Type Management</strong> - Create customizable meeting templates</li>
                <li><strong>Smart Availability</strong> - Automatic conflict detection across multiple calendars</li>
                <li><strong>Calendar Integration</strong> - Sync with Google Calendar, Outlook, and iCloud</li>
                <li><strong>Public Booking Pages</strong> - Beautiful, branded booking experience</li>
                <li><strong>Real-time Notifications</strong> - Email and SMS reminders</li>
                <li><strong>Analytics</strong> - Track booking metrics and conversion rates</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>Base URL</h3>
              <CodeBlock
                code={`Production: https://api-gateway.internal.mernaki.de/api/scheduler
Development: https://api-gateway.internal.mernaki.de/api/scheduler`}
              />
              <p className="text-sm" style={{ color: '#999999' }}>
                All requests go through the main Mernaki backend for security and logging.
              </p>
            </Section>

            {/* Authentication Section */}
            <Section title="Authentication" id="authentication">
              <p className="mb-4">
                Most endpoints require authentication using JWT tokens. Public booking endpoints (viewing meeting types, creating bookings) do not require authentication.
              </p>

              <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>Headers</h3>
              <CodeBlock
                code={`Authorization: Bearer YOUR_JWT_TOKEN
Content-Type: application/json`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-6" style={{ color: '#1A1A1A' }}>Getting a Token</h3>
              <p className="mb-4">Login through the dashboard to receive JWT tokens:</p>
              <CodeBlock
                code={`POST /api/auth/login
{
  "email": "your@email.com",
  "password": "your-password"
}`}
              />

              <p className="mb-2 font-semibold">Response:</p>
              <CodeBlock
                code={`{
  "accessToken": "eyJhbGc...",
  "refreshToken": "eyJhbGc...",
  "user": { ... }
}`}
              />
            </Section>

            {/* Meeting Types Section */}
            <Section title="Meeting Types" id="meeting-types">
              <p className="mb-6">
                Meeting types are templates that define the structure of your meetings (duration, location, availability rules).
              </p>

              <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>List Meeting Types</h3>
              <p className="mb-4">Get all active meeting types (public endpoint).</p>
              <CodeBlock code="GET /api/scheduler/meeting-types" />

              <p className="mb-2 font-semibold">Response:</p>
              <CodeBlock
                code={`{
  "meetingTypes": [
    {
      "id": "uuid",
      "title": "30-Minute Consultation",
      "description": "Quick consultation call",
      "durationMinutes": 30,
      "bufferBefore": 5,
      "bufferAfter": 5,
      "locationType": "zoom",
      "customUrl": "consultation",
      "isActive": true
    }
  ]
}`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Create Meeting Type</h3>
              <p className="mb-4">Create a new meeting type (requires authentication).</p>
              <CodeBlock
                code={`POST /api/scheduler/meeting-types
Authorization: Bearer YOUR_TOKEN`}
              />

              <p className="mb-2 font-semibold">Request Body:</p>
              <CodeBlock
                code={`{
  "title": "Technical Interview",
  "description": "1-hour technical assessment",
  "durationMinutes": 60,
  "bufferBefore": 10,
  "bufferAfter": 10,
  "locationType": "zoom",
  "customUrl": "tech-interview"
}`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Get Meeting Type by ID</h3>
              <CodeBlock code="GET /api/scheduler/meeting-types/:id" />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Update Meeting Type</h3>
              <CodeBlock
                code={`PATCH /api/scheduler/meeting-types/:id
Authorization: Bearer YOUR_TOKEN`}
              />
              <p className="mb-2 font-semibold">Request Body (partial update):</p>
              <CodeBlock
                code={`{
  "title": "Updated Title",
  "isActive": false
}`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Delete Meeting Type</h3>
              <CodeBlock
                code={`DELETE /api/scheduler/meeting-types/:id
Authorization: Bearer YOUR_TOKEN`}
              />
            </Section>

            {/* Availability Section */}
            <Section title="Availability" id="availability">
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>Get Available Slots</h3>
              <p className="mb-4">Get available time slots for a meeting type.</p>
              <CodeBlock code="GET /api/scheduler/availability/slots" />

              <p className="mb-2 font-semibold">Query Parameters:</p>
              <ul className="list-disc list-inside space-y-2 mb-4">
                <li><code>meetingTypeId</code> (required) - Meeting type ID</li>
                <li><code>from</code> (required) - Start date/time (ISO 8601)</li>
                <li><code>to</code> (required) - End date/time (ISO 8601)</li>
                <li><code>tz</code> (optional) - Timezone (default: UTC)</li>
              </ul>

              <p className="mb-2 font-semibold">Example:</p>
              <CodeBlock
                code="GET /api/scheduler/availability/slots?meetingTypeId=uuid&from=2024-01-01T00:00:00Z&to=2024-01-07T23:59:59Z&tz=America/New_York"
              />

              <p className="mb-2 font-semibold">Response:</p>
              <CodeBlock
                code={`{
  "slots": [
    {
      "startTs": "2024-01-02T14:00:00Z",
      "endTs": "2024-01-02T14:30:00Z",
      "ownerId": "uuid",
      "ownerName": "John Doe"
    }
  ]
}`}
              />
            </Section>

            {/* Bookings Section */}
            <Section title="Bookings" id="bookings">
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>Create Booking</h3>
              <p className="mb-4">Create a new booking (public endpoint).</p>
              <CodeBlock code="POST /api/scheduler/bookings" />

              <p className="mb-2 font-semibold">Request Body:</p>
              <CodeBlock
                code={`{
  "meetingTypeId": "uuid",
  "startTs": "2024-01-02T14:00:00Z",
  "candidate": {
    "name": "Jane Smith",
    "email": "jane@example.com",
    "phone": "+1234567890"
  },
  "timezone": "America/New_York"
}`}
              />

              <p className="mb-2 font-semibold">Response:</p>
              <CodeBlock
                code={`{
  "booking": {
    "id": "uuid",
    "status": "confirmed",
    "startTs": "2024-01-02T14:00:00Z",
    "endTs": "2024-01-02T14:30:00Z"
  }
}`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Get Booking</h3>
              <CodeBlock code="GET /api/scheduler/bookings/:id" />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Cancel Booking</h3>
              <CodeBlock code="POST /api/scheduler/bookings/:id/cancel" />

              <h3 className="text-xl font-semibold mb-3 mt-6" style={{ color: '#1A1A1A' }}>Reschedule Booking</h3>
              <CodeBlock code="POST /api/scheduler/bookings/:id/reschedule" />
            </Section>

            {/* Notifications Section */}
            <Section title="Notifications" id="notifications">
              <p className="mb-6">
                Comprehensive notification system with in-app notifications, customizable templates, sequences, and advanced features like push notifications, snoozing, and real-time updates.
              </p>

              <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>In-App Notifications</h3>

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Get Notifications</h4>
              <p className="mb-4">Fetch user notifications with pagination and filtering.</p>
              <CodeBlock
                code={`GET /api/notifications?limit=50&offset=0&type=booking&unreadOnly=true
Authorization: Bearer YOUR_TOKEN`}
              />
              <p className="mb-2 font-semibold">Query Parameters:</p>
              <ul className="list-disc list-inside mb-4 space-y-1">
                <li><code>limit</code> (optional): Number of notifications to return (default: 50)</li>
                <li><code>offset</code> (optional): Pagination offset (default: 0)</li>
                <li><code>type</code> (optional): Filter by type (booking, system, reminder, etc.)</li>
                <li><code>unreadOnly</code> (optional): Show only unread notifications (true/false)</li>
              </ul>

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Get Unread Count</h4>
              <CodeBlock code={`GET /api/notifications/unread-count
Authorization: Bearer YOUR_TOKEN`} />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Mark as Read</h4>
              <CodeBlock code={`POST /api/notifications/:id/read
Authorization: Bearer YOUR_TOKEN`} />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Mark All as Read</h4>
              <CodeBlock code={`POST /api/notifications/read-all
Authorization: Bearer YOUR_TOKEN`} />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Delete Notification</h4>
              <CodeBlock code={`DELETE /api/notifications/:id
Authorization: Bearer YOUR_TOKEN`} />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Delete All Notifications</h4>
              <p className="mb-4">Deletes all non-important notifications.</p>
              <CodeBlock code={`DELETE /api/notifications
Authorization: Bearer YOUR_TOKEN`} />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Export to PDF</h4>
              <p className="mb-4">Export notifications as a PDF document.</p>
              <CodeBlock code={`GET /api/notifications/export/pdf?type=booking
Authorization: Bearer YOUR_TOKEN`} />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Notification Templates & Studio</h3>

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Get All Templates</h4>
              <CodeBlock code={`GET /api/notifications/templates
Authorization: Bearer YOUR_TOKEN`} />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Get Template</h4>
              <CodeBlock code={`GET /api/notifications/templates/:id
Authorization: Bearer YOUR_TOKEN`} />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Create Template</h4>
              <CodeBlock
                code={`POST /api/notifications/templates
Authorization: Bearer YOUR_TOKEN`}
              />
              <p className="mb-2 font-semibold">Request Body:</p>
              <CodeBlock
                code={`{
  "name": "Booking Confirmation",
  "type": "email",  // or "sms", "in-app", "push"
  "trigger": "booking_created",
  "subject": "Your booking is confirmed!",
  "body": "Hi {{candidateName}}, your booking for {{meetingType}} is confirmed.",
  "htmlBody": "<p>Hi {{candidateName}}, your booking is confirmed.</p>",
  "isActive": true
}`}
              />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Update Template</h4>
              <CodeBlock code={`PUT /api/notifications/templates/:id
Authorization: Bearer YOUR_TOKEN`} />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Delete Template</h4>
              <CodeBlock code={`DELETE /api/notifications/templates/:id
Authorization: Bearer YOUR_TOKEN`} />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Duplicate Template</h4>
              <CodeBlock
                code={`POST /api/notifications/templates/:id/duplicate
Authorization: Bearer YOUR_TOKEN

{
  "name": "Copy of Template Name"
}`}
              />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Preview Template</h4>
              <p className="mb-4">Preview a template with sample variables.</p>
              <CodeBlock
                code={`POST /api/notifications/templates/:id/preview
Authorization: Bearer YOUR_TOKEN

{
  "variables": {
    "candidateName": "John Doe",
    "meetingType": "Interview",
    "date": "2026-12-15"
  }
}`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Notification Sequences</h3>

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Get All Sequences</h4>
              <CodeBlock code={`GET /api/notifications/sequences
Authorization: Bearer YOUR_TOKEN`} />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Create Sequence</h4>
              <p className="mb-4">Create an automated notification sequence.</p>
              <CodeBlock
                code={`POST /api/notifications/sequences
Authorization: Bearer YOUR_TOKEN

{
  "name": "Interview Reminder Sequence",
  "trigger": "booking_created",
  "steps": [
    {
      "delay": 86400,  // 24 hours in seconds
      "templateId": "template-uuid",
      "condition": "booking.status === 'confirmed'"
    },
    {
      "delay": 3600,  // 1 hour before
      "templateId": "reminder-template-uuid"
    }
  ]
}`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Advanced Notifications</h3>

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Real-Time Notifications (SSE)</h4>
              <p className="mb-4">Subscribe to real-time notification updates via Server-Sent Events.</p>
              <CodeBlock code={`GET /api/advanced-notifications/stream
Authorization: Bearer YOUR_TOKEN`} />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Get Notification Preferences</h4>
              <CodeBlock code={`GET /api/advanced-notifications/preferences
Authorization: Bearer YOUR_TOKEN`} />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Update Preferences</h4>
              <CodeBlock
                code={`PUT /api/advanced-notifications/preferences
Authorization: Bearer YOUR_TOKEN

{
  "emailEnabled": true,
  "smsEnabled": false,
  "pushEnabled": true,
  "inAppEnabled": true,
  "quietHoursStart": "22:00",
  "quietHoursEnd": "08:00"
}`}
              />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Snooze Notification</h4>
              <p className="mb-4">Snooze a notification until a specific time.</p>
              <CodeBlock
                code={`POST /api/advanced-notifications/:id/snooze
Authorization: Bearer YOUR_TOKEN

{
  "until": "2026-12-15T10:00:00Z"
}`}
              />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Unsnooze Notification</h4>
              <CodeBlock code={`POST /api/advanced-notifications/:id/unsnooze
Authorization: Bearer YOUR_TOKEN`} />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Get Active Notifications</h4>
              <p className="mb-4">Get all active (non-snoozed, unread) notifications.</p>
              <CodeBlock code={`GET /api/advanced-notifications/active
Authorization: Bearer YOUR_TOKEN`} />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Push Notification Subscription</h4>
              <p className="mb-4">Subscribe to web push notifications.</p>
              <CodeBlock
                code={`POST /api/advanced-notifications/push/subscribe
Authorization: Bearer YOUR_TOKEN

{
  "subscription": {
    "endpoint": "https://fcm.googleapis.com/...",
    "keys": {
      "p256dh": "...",
      "auth": "..."
    }
  }
}`}
              />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Unsubscribe from Push</h4>
              <CodeBlock
                code={`POST /api/advanced-notifications/push/unsubscribe
Authorization: Bearer YOUR_TOKEN

{
  "endpoint": "https://fcm.googleapis.com/..."
}`}
              />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Get VAPID Public Key</h4>
              <p className="mb-4">Get the VAPID public key for push notification setup.</p>
              <CodeBlock code="GET /api/advanced-notifications/push/vapid-key" />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Track Notification Interaction</h4>
              <p className="mb-4">Track when a notification is clicked or interacted with.</p>
              <CodeBlock
                code={`POST /api/advanced-notifications/:id/track
Authorization: Bearer YOUR_TOKEN

{
  "action": "clicked"  // or "dismissed", "viewed"
}`}
              />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Get Analytics</h4>
              <p className="mb-4">Get notification analytics and metrics.</p>
              <CodeBlock code={`GET /api/advanced-notifications/analytics?fromDate=2026-12-01&toDate=2026-12-31
Authorization: Bearer YOUR_TOKEN`} />

              <h4 className="text-lg font-semibold mb-2 mt-6" style={{ color: '#1A1A1A' }}>Get Notification Groups</h4>
              <p className="mb-4">Get notifications grouped by type or category.</p>
              <CodeBlock code={`GET /api/advanced-notifications/groups
Authorization: Bearer YOUR_TOKEN`} />
            </Section>

            {/* AI Summarization Section */}
            <Section title="AI Summarization" id="ai-summarization">
              <p className="mb-6">
                Manage AI-generated meeting summaries and configuration. These endpoints allow you to save, retrieve, and delete summaries, as well as configure AI settings.
              </p>

              <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>Save Meeting Summary</h3>
              <p className="mb-4">Save a generated summary for a specific booking.</p>
              <CodeBlock
                code={`POST /api/meetings/:bookingId/summary
Authorization: Bearer YOUR_TOKEN`}
              />
              <p className="mb-2 font-semibold">Request Body:</p>
              <CodeBlock
                code={`{
  "summaryText": "Meeting overview...",
  "keyPoints": ["Point 1", "Point 2"],
  "actionItems": ["Action 1"],
  "decisions": ["Decision 1"],
  "transcript": "Full transcript...",
  "durationSeconds": 1800
}`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Get Meeting Summary</h3>
              <p className="mb-4">Retrieve the saved summary for a booking.</p>
              <CodeBlock
                code={`GET /api/meetings/:bookingId/summary
Authorization: Bearer YOUR_TOKEN`}
              />
              <p className="mb-2 font-semibold">Response:</p>
              <CodeBlock
                code={`{
  "summary": {
    "id": "uuid",
    "bookingId": "uuid",
    "summaryText": "Meeting overview...",
    "keyPoints": ["Point 1", "Point 2"],
    "actionItems": ["Action 1"],
    "decisions": ["Decision 1"],
    "createdAt": "2024-01-02T14:30:00Z"
  }
}`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Delete Meeting Summary</h3>
              <CodeBlock
                code={`DELETE /api/meetings/:bookingId/summary
Authorization: Bearer YOUR_TOKEN`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Get AI Configuration</h3>
              <p className="mb-4">Get current AI settings including meeting summarization preferences.</p>
              <CodeBlock
                code={`GET /api/ai/config
Authorization: Bearer YOUR_TOKEN`}
              />
              <p className="mb-2 font-semibold">Response:</p>
              <CodeBlock
                code={`{
  "configured": true,
  "config": {
    "provider": "ollama",
    "modelName": "llama3:8b",
    "meetingNotesEnabled": true,
    "meetingAiAutoLoadModels": true,
    "meetingAiRememberPermission": true,
    "meetingAiAutoSave": false
  }
}`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Update AI Configuration</h3>
              <p className="mb-4">Configure AI provider and meeting summarization preferences.</p>
              <CodeBlock
                code={`POST /api/ai/config
Authorization: Bearer YOUR_TOKEN`}
              />
              <p className="mb-2 font-semibold">Request Body:</p>
              <CodeBlock
                code={`{
  "provider": "ollama",  // Options: "ollama", "lm-studio", "llama-cpp", "gpt4all", "jan", "kobold-cpp", "mlx", "vllm", "custom", "disabled"
  "endpointUrl": "http://localhost:11434",  // Provider-specific endpoint
  "modelName": "llama3:8b",  // Model to use for generation
  "apiKey": "optional_api_key",  // Optional API key for secured endpoints
  "meetingNotesEnabled": true,
  "meetingAiAutoLoadModels": true,
  "meetingAiRememberPermission": true,
  "meetingAiAutoSave": true
}`}
              />
              <p className="mb-2 mt-4 font-semibold">Supported Providers & Default Endpoints:</p>
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(0, 0, 0, 0.05)' }}>
                      <th className="p-3 text-left font-semibold">Provider</th>
                      <th className="p-3 text-left font-semibold">Default Endpoint</th>
                      <th className="p-3 text-left font-semibold">API Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['ollama', 'http://localhost:11434', 'Ollama API'],
                      ['lm-studio', 'http://localhost:1234/v1', 'OpenAI-compatible'],
                      ['llama-cpp', 'http://localhost:8080/v1', 'OpenAI-compatible'],
                      ['gpt4all', 'http://localhost:4891/v1', 'OpenAI-compatible'],
                      ['jan', 'http://localhost:1337/v1', 'OpenAI-compatible'],
                      ['kobold-cpp', 'http://localhost:5001/v1', 'OpenAI-compatible'],
                      ['mlx', 'http://localhost:8080/v1', 'OpenAI-compatible'],
                      ['vllm', 'http://localhost:8000/v1', 'OpenAI-compatible'],
                      ['custom', 'Custom URL', 'OpenAI-compatible'],
                      ['disabled', 'N/A', 'N/A']
                    ].map(([provider, endpoint, apiType], i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.05)' }}>
                        <td className="p-3"><code>{provider}</code></td>
                        <td className="p-3">{endpoint}</td>
                        <td className="p-3">{apiType}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Generate Summary (Server-Side)</h3>
              <p className="mb-4">Generate a summary using server-side AI (if configured).</p>
              <CodeBlock
                code={`POST /api/ai/generate/meeting-summary
Authorization: Bearer YOUR_TOKEN`}
              />
              <p className="mb-2 font-semibold">Request Body:</p>
              <CodeBlock
                code={`{
  "transcript": "Full meeting transcript...",
  "meetingType": "general",
  "title": "Weekly Sync",
  "participants": ["John", "Jane"]
}`}
              />
            </Section>

            {/* API Key Management Section */}
            <Section title="API Key Management" id="api-keys">
              <p className="mb-6">
                Programmatically manage API keys for your organization. You can create, revoke, and monitor usage of API keys.
              </p>

              <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>List API Keys</h3>
              <CodeBlock
                code={`GET /api/api-keys
Authorization: Bearer YOUR_TOKEN`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Create API Key</h3>
              <p className="mb-4">Create a new API key with specific permissions.</p>
              <CodeBlock
                code={`POST /api/api-keys
Authorization: Bearer YOUR_TOKEN`}
              />
              <p className="mb-2 font-semibold">Request Body:</p>
              <CodeBlock
                code={`{
  "name": "Production Key",
  "permissions": ["read:bookings", "write:bookings"],
  "expiresInDays": 365
}`}
              />
              <p className="mb-2 font-semibold">Response (Key returned only once):</p>
              <CodeBlock
                code={`{
  "apiKey": { "id": "...", "name": "Production Key" },
  "key": "jm_sk_..." 
}`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Delete API Key</h3>
              <CodeBlock
                code={`DELETE /api/api-keys/:id
Authorization: Bearer YOUR_TOKEN`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Get Key Usage</h3>
              <p className="mb-4">View usage statistics for a specific key.</p>
              <CodeBlock
                code={`GET /api/api-keys/:id/usage
Authorization: Bearer YOUR_TOKEN`}
              />
            </Section>

            {/* Smart Forms Section */}
            <Section title="Smart Forms" id="smart-forms">
              <p className="mb-6">
                Manage AI-powered forms for qualifying leads and collecting candidate information.
              </p>

              <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>List Forms</h3>
              <CodeBlock
                code={`GET /api/forms
Authorization: Bearer YOUR_TOKEN`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Get Form</h3>
              <p className="mb-4">Get form details (Public endpoint for rendering).</p>
              <CodeBlock
                code={`GET /api/forms/:id`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Create Form</h3>
              <CodeBlock
                code={`POST /api/forms
Authorization: Bearer YOUR_TOKEN`}
              />
              <p className="mb-2 font-semibold">Request Body:</p>
              <CodeBlock
                code={`{
  "name": "Qualification Form",
  "type": "qualification",
  "fields": [
    {
      "id": "role",
      "type": "text",
      "label": "Current Role",
      "required": true
    }
  ]
}`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Submit Response</h3>
              <p className="mb-4">Submit a response to a form (Public).</p>
              <CodeBlock
                code={`POST /api/forms/:id/submit`}
              />
              <p className="mb-2 font-semibold">Request Body:</p>
              <CodeBlock
                code={`{
  "bookingId": "uuid",
  "responses": {
    "role": "Senior Developer"
  }
}`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Get Submissions</h3>
              <CodeBlock
                code={`GET /api/forms/:id/submissions
Authorization: Bearer YOUR_TOKEN`}
              />
            </Section>

            {/* Automation Workflows Section */}
            <Section title="Automation Workflows" id="workflows">
              <p className="mb-6">
                Build and execute automated workflows triggered by scheduling events.
              </p>

              <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>List Workflows</h3>
              <CodeBlock
                code={`GET /api/automation/workflows
Authorization: Bearer YOUR_TOKEN`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Create Workflow</h3>
              <CodeBlock
                code={`POST /api/automation/workflows
Authorization: Bearer YOUR_TOKEN`}
              />
              <p className="mb-2 font-semibold">Request Body:</p>
              <CodeBlock
                code={`{
  "name": "Post-Booking Email",
  "trigger": "booking_created",
  "nodes": [
    { "type": "delay", "config": { "seconds": 3600 } },
    { "type": "email", "config": { "templateId": "..." } }
  ]
}`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Manually Execute Workflow</h3>
              <p className="mb-4">Trigger a workflow manually (useful for testing).</p>
              <CodeBlock
                code={`POST /api/automation/workflows/:id/execute
Authorization: Bearer YOUR_TOKEN`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Get Executions</h3>
              <p className="mb-4">View execution history and logs.</p>
              <CodeBlock
                code={`GET /api/automation/workflows/:id/executions
Authorization: Bearer YOUR_TOKEN`}
              />
            </Section>

            {/* Webhooks Section */}
            <Section title="Webhooks" id="webhooks">
              <p className="mb-6">
                Subscribe to real-time events like bookings, cancellations, and form submissions.
              </p>

              <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>Register Webhook</h3>
              <CodeBlock
                code={`POST /api/webhooks
Authorization: Bearer YOUR_TOKEN`}
              />
              <p className="mb-2 font-semibold">Request Body:</p>
              <CodeBlock
                code={`{
  "url": "https://your-api.com/webhook",
  "events": ["booking.created", "booking.cancelled"],
  "secret": "signing_secret_123"
}`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>List Webhooks</h3>
              <CodeBlock
                code={`GET /api/webhooks
Authorization: Bearer YOUR_TOKEN`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>View Delivery History</h3>
              <p className="mb-4">Debug webhook deliveries and view response codes.</p>
              <CodeBlock
                code={`GET /api/webhooks/:id/deliveries
Authorization: Bearer YOUR_TOKEN`}
              />
            </Section>

            {/* Analytics Section */}
            <Section title="Analytics" id="analytics">
              <p className="mb-6">
                Retrieve tracking data, conversion metrics, and booking statistics.
              </p>

              <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>Get Overview Metrics</h3>
              <CodeBlock
                code={`GET /api/analytics?fromDate=2026-01-01&toDate=2026-01-31
Authorization: Bearer YOUR_TOKEN`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Export Data (CSV)</h3>
              <p className="mb-4">Download comprehensive booking data as CSV.</p>
              <CodeBlock
                code={`GET /api/analytics/export
Authorization: Bearer YOUR_TOKEN`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Track Configurable View</h3>
              <p className="mb-4">Public endpoint to track views on custom booking pages.</p>
              <CodeBlock
                code={`POST /api/analytics/track-view`}
              />
              <p className="mb-2 font-semibold">Request Body:</p>
              <CodeBlock
                code={`{
  "ownerId": "uuid",
  "meetingTypeId": "uuid",
  "metadata": { "source": "email_campaign" }
}`}
              />
            </Section>

            {/* Integrations Section */}
            <Section title="Integrations" id="integrations">
              <p className="mb-6">
                Manage connected calendar providers (Google, Outlook, CalDAV, Zoom, Webex).
              </p>

              <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>List Integrations</h3>
              <CodeBlock
                code={`GET /api/integrations
Authorization: Bearer YOUR_TOKEN`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Delete Integration</h3>
              <p className="mb-4">Disconnects a provider and revokes access tokens.</p>
              <CodeBlock
                code={`DELETE /api/integrations/:id
Authorization: Bearer YOUR_TOKEN`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Connect CalDAV (iCloud/Other)</h3>
              <CodeBlock
                code={`POST /api/integrations/caldav/connect
Authorization: Bearer YOUR_TOKEN`}
              />
              <p className="mb-2 font-semibold">Request Body:</p>
              <CodeBlock
                code={`{
  "serverUrl": "https://caldav.icloud.com",
  "username": "user@icloud.com",
  "password": "app-specific-password"
}`}
              />
            </Section>

            {/* User Database Configuration Section */}
            <Section title="User Database Configuration (BYODB)" id="user-db-config">
              <p className="mb-6">
                Configure your own PostgreSQL database to store sensitive data (bookings, candidates, forms).
                The system will automatically route read/write operations to your database.
              </p>

              <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>Get Configuration</h3>
              <CodeBlock
                code={`GET /api/scheduler/user-db-config
Authorization: Bearer YOUR_TOKEN`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Save Configuration</h3>
              <CodeBlock
                code={`POST /api/scheduler/user-db-config
Authorization: Bearer YOUR_TOKEN`}
              />
              <p className="mb-2 font-semibold">Request Body:</p>
              <CodeBlock
                code={`{
  "host": "db.example.com",
  "port": 5432,
  "database": "mernaki_user_db",
  "username": "user",
  "password": "secure_password",
  "isActive": true
}`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-8" style={{ color: '#1A1A1A' }}>Test Connection</h3>
              <CodeBlock
                code={`POST /api/scheduler/user-db-config/test
Authorization: Bearer YOUR_TOKEN`}
              />
              <p className="mb-2 font-semibold">Request Body:</p>
              <CodeBlock
                code={`{
  "host": "db.example.com",
  ...
}`}
              />
            </Section>

            {/* Self-Hosting Section */}
            <Section title="Self-Hosting" id="self-hosting">
              <p className="mb-6">
                Easily set up a compatible PostgreSQL database using our Docker template.
              </p>

              <h3 className="text-xl font-semibold mb-3" style={{ color: '#1A1A1A' }}>Get Installation Script</h3>
              <p className="mb-4">Fetch the automated setup script (public endpoint).</p>
              <CodeBlock code="GET /docker/user-db" />

              <p className="mb-2 font-semibold">Quick Start:</p>
              <CodeBlock
                code="curl -s https://api-gateway.internal.mernaki.de/docker/user-db | bash"
                language="bash"
              />
            </Section>

            {/* Error Handling Section */}
            <Section title="Error Handling" id="errors">
              <p className="mb-4">All errors follow a consistent format:</p>
              <CodeBlock
                code={`{
  "error": {
    "code": "validation_error",
    "message": "Missing required field: email",
    "retryable": false
  }
}`}
              />

              <h3 className="text-xl font-semibold mb-3 mt-6" style={{ color: '#1A1A1A' }}>Common Error Codes</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(0, 0, 0, 0.05)' }}>
                      <th className="p-3 text-left font-semibold">Code</th>
                      <th className="p-3 text-left font-semibold">Status</th>
                      <th className="p-3 text-left font-semibold">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['validation_error', '400', 'Invalid request data'],
                      ['unauthorized', '401', 'Missing or invalid authentication'],
                      ['not_found', '404', 'Resource not found'],
                      ['conflict', '409', 'Resource conflict'],
                      ['rate_limit_exceeded', '429', 'Too many requests'],
                      ['internal_error', '500', 'Server error']
                    ].map(([code, status, desc], i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.05)' }}>
                        <td className="p-3"><code>{code}</code></td>
                        <td className="p-3">{status}</td>
                        <td className="p-3">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            {/* Rate Limits Section */}
            <Section title="Rate Limits" id="rate-limits">
              <p className="mb-4">To ensure fair usage and system stability:</p>
              <ul className="list-disc list-inside space-y-2 mb-6">
                <li><strong>Public endpoints</strong>: 100 requests per 15 minutes per IP</li>
                <li><strong>Authenticated endpoints</strong>: 1000 requests per 15 minutes per user</li>
                <li><strong>Auth endpoints</strong>: 10 requests per 15 minutes per IP</li>
              </ul>

              <p className="mb-2">Rate limit headers are included in responses:</p>
              <CodeBlock
                code={`X-RateLimit-Limit: 100
X-RateLimit-Remaining: 95
X-RateLimit-Reset: 1640000000`}
              />
            </Section>

            {/* Support Section */}
            <div className="mt-16 p-6 rounded-xl" style={{ backgroundColor: 'rgba(74, 144, 226, 0.1)', border: '1px solid rgba(74, 144, 226, 0.2)' }}>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#4A90E2' }}>Need Help?</h3>
              <p className="mb-4">Contact our API support team:</p>
              <ul className="space-y-2">
                <li><strong>Email:</strong> developer@mernaki.de</li>
                <li><strong>Status:</strong> <a href="https://status.mernaki.com" style={{ color: '#4A90E2' }}>status.mernaki.com</a></li>
                <li><strong>Dashboard:</strong> <a href="http://localhost:3010/dashboard" style={{ color: '#4A90E2' }}>localhost:3010/dashboard</a></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <FooterSection isMobile={isMobile} />
    </>
  )
}
